import * as React from 'react';

import {
  enumDocumentType,
  enumLoadSortOption,
  LoadSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  DataGrid,
  FloatingToolbar,
  useMemoDataGridRows,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideDownload, LucideX } from 'lucide-react';

import * as state from '../../_state/transactions.state';
import { usePrintWithBrowser } from '../../hooks/usePrintWithBrowser';
import { useTranslations } from '../../i18n/useTranslations';
import { OutboundTransactionRecord } from '../../types/transaction';

import { useTransactionsOutboundColumns } from './hooks/useTransactionsOutboundColumns';
import { TransactionSidePanel } from './TransactionSidePanel';

interface OutboundSortProps {
  asc: LoadSortOption;
  desc: LoadSortOption;
}

const OUTBOUND_SORT_MAP: Record<string, OutboundSortProps> = {
  status: {
    asc: enumLoadSortOption.StatusAsc,
    desc: enumLoadSortOption.StatusDesc,
  },
  shippedDate: {
    asc: enumLoadSortOption.ShippedDateAsc,
    desc: enumLoadSortOption.ShippedDateDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ TRANSACTIONS OUTBOUND DATA GRID ⚛️ ⚛️ ⚛️
//
export const TransactionsOutboundDataGrid = () => {
  // Translations
  const t = useTranslations();

  const [sidePanelOpen, setSidePanelOpen] = React.useState(false);
  const [sidePanelDocLabel, setSidePanelDocLabel] = React.useState('');

  const setSelectedOutboundId = useSetAtom(
    state.$selectedOutboundTransactionId,
  );
  const setOutboundSorting = useSetAtom(state.$outboundSorting);
  const setOffset = useSetAtom(state.$offset);
  const hasFiltersActive = useAtomValue(state.$filtersActive);
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedOutboundKeys);

  // Data hooks
  const {
    data: outboundData,
    isFetching,
    loadingState,
    error,
    refetch,
  } = useAtomValue(state.$remoteOutbound);
  const { print, isPrinting } = usePrintWithBrowser();

  // Memos
  const tickets = outboundData?.loads ?? [];

  const handleDoubleClick = React.useCallback(
    (row: OutboundTransactionRecord) => {
      setSelectedOutboundId(row.id);
      setSidePanelDocLabel(row.loadNumber);
      setSidePanelOpen(true);
    },
    [setSelectedOutboundId],
  );

  const { columns, columnVisibilityState } = useTransactionsOutboundColumns({
    onDoubleClick: handleDoubleClick,
    onRefetch: refetch,
  });

  const selectedRows = useMemoDataGridRows(tickets, selectedKeys);

  // Callbacks
  const handleColumnSort = React.useCallback(
    (columnId: string, sortDirection: 'ascending' | 'descending' | null) => {
      if (sortDirection === null) {
        setOutboundSorting([enumLoadSortOption.CreatedAtDesc]);
      } else {
        const sortMap = OUTBOUND_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            sortDirection === 'ascending' ? sortMap.asc : sortMap.desc;
          setOutboundSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setOutboundSorting, setOffset],
  );

  const handleBulkDownload = React.useCallback(async () => {
    const documents = selectedRows.flatMap((row) => [
      { documentType: enumDocumentType.Invoice, loadId: row.id },
      { documentType: enumDocumentType.PackingList, loadId: row.id },
      { documentType: enumDocumentType.BillOfLading, loadId: row.id },
    ]);

    await print({ documents, action: 'download' });
    setSelectedKeys(new Set<React.Key>());
  }, [selectedRows, print, setSelectedKeys]);

  return (
    <>
      <DataGrid<OutboundTransactionRecord>
        isLoading={loadingState.isInitialLoading || isFetching}
        columns={columns}
        dataSource={tickets}
        rowKey="id"
        aria-label={t.transactionsView.table.aria}
        selectionMode="multiple"
        onSelectionChange={setSelectedKeys}
        onColumnSort={handleColumnSort}
        columnVisibilityState={columnVisibilityState}
        className="w-full text-sm"
        fullWidth
        persistColumnOrderKey="trade:transactions-outbound"
        hasError={!!error}
        errorPanelProps={
          error
            ? {
                description: t.transactionsView.content.error(error.message),
                centered: true,
              }
            : undefined
        }
        emptyPanelProps={
          hasFiltersActive
            ? {
                title: t.transactionsView.emptyResultsState.title,
                description: t.transactionsView.emptyResultsState.subtitle,
                centered: true,
              }
            : {
                title: t.transactionsView.emptyState.title,
                description: t.transactionsView.emptyState.subtitle,
                centered: true,
              }
        }
      />
      <FloatingToolbar visible={selectedRows.length > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>{' '}
            {t.transactionsView.toolbar.selected}
          </div>
          <Button
            variant="outline"
            icon={<LucideDownload />}
            isDisabled={selectedRows.length < 1 || isPrinting}
            isLoading={isPrinting}
            onPress={handleBulkDownload}
          >
            {t.transactionsView.toolbar.downloadDocuments(selectedRows.length)}
          </Button>
          <LucideX
            className="size-4 cursor-pointer text-neutral/80"
            onClick={() => setSelectedKeys(new Set<React.Key>())}
          />
        </div>
      </FloatingToolbar>
      <TransactionSidePanel
        direction="outbound"
        documentLabel={sidePanelDocLabel}
        isOpen={sidePanelOpen}
        onOpenChange={setSidePanelOpen}
      />
    </>
  );
};
